import type { ResumoDashboard } from "@base/contracts";
import { ExternalLink, Send } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  formatarDataCurta,
  formatarNumero,
  ROTULOS_PLATAFORMA,
  ROTULOS_STATUS,
} from "./dashboard-formatadores";
import {
  ChipStatus,
  MetricaCompacta,
  NumeroHero,
  Painel,
  PainelCabecalho,
  PainelCorpo,
} from "./dashboard-ui";

export function AbaPublicacao({
  resumo,
}: {
  readonly resumo: ResumoDashboard;
}) {
  const pub = resumo.publicacaoResumo;
  const plataformas = Object.entries(pub.porPlataforma);
  const status = Object.entries(pub.porStatus);
  const agendados =
    (pub.porStatus.scheduled ?? 0) + (pub.porStatus.queued ?? 0);

  return (
    <div className="flex flex-col gap-8">
      <Painel className="dashboard-hero" aria-label="Saúde da agenda">
        <div className="relative z-[1] grid gap-0 md:grid-cols-12">
          <div className="flex flex-col justify-between gap-6 border-b border-border/60 p-6 sm:p-8 md:col-span-5 md:border-b-0 md:border-r">
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">
                Falhas abertas
              </p>
              <NumeroHero tom={pub.totalFalhas > 0 ? "alerta" : "padrao"}>
                {formatarNumero(pub.totalFalhas)}
              </NumeroHero>
              {pub.totalFalhas > 0 ? (
                <a
                  href="#falhas-publicacao"
                  className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-[color:var(--rk-text-brand)] underline-offset-4 hover:underline"
                >
                  Abrir fila de falhas
                  <ExternalLink className="size-3.5" aria-hidden="true" />
                </a>
              ) : (
                <p className="text-sm text-muted-foreground">Fila limpa.</p>
              )}
            </div>
            <ChipStatus
              tom={resumo.performance.metaConectada ? "ok" : "alerta"}
            >
              {resumo.performance.metaConectada
                ? "Facebook e Instagram conectados"
                : "Redes desconectadas"}
            </ChipStatus>
          </div>

          <div className="grid grid-cols-1 divide-y divide-border/60 sm:grid-cols-3 sm:divide-x sm:divide-y-0 md:col-span-7">
            <div className="px-6 py-5 md:py-8">
              <MetricaCompacta
                rotulo="Publicados"
                valor={formatarNumero(pub.porStatus.published ?? 0)}
                detalhe="Na agenda"
              />
            </div>
            <div className="px-6 py-5 md:py-8">
              <MetricaCompacta
                rotulo="Agendados"
                valor={formatarNumero(agendados)}
                detalhe="Na fila ou marcados"
              />
            </div>
            <div className="px-6 py-5 md:py-8">
              <MetricaCompacta
                rotulo="Última coleta"
                valor={
                  resumo.performance.ultimaColetaEm
                    ? formatarDataCurta(resumo.performance.ultimaColetaEm)
                    : "Nunca"
                }
                detalhe="Insights dos últimos 30 dias"
              />
            </div>
          </div>
        </div>
      </Painel>

      <Painel>
        <PainelCabecalho
          titulo="Coleta de Insights"
          descricao="Números do Instagram dos últimos 30 dias, gravados em histórico diário."
        />
        <PainelCorpo>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Última coleta:{" "}
            <span className="font-medium text-foreground tabular-nums">
              {formatarDataCurta(resumo.performance.ultimaColetaEm)}
            </span>
            . Use Coletar agora no topo se precisar atualizar antes do job do
            dia.
          </p>
          {!resumo.performance.metaConectada ? (
            <a
              href="#configuracoes?secao=publicacao"
              className="mt-4 inline-flex cursor-pointer text-sm font-medium underline-offset-4 hover:underline"
            >
              Conectar em Redes do cliente
            </a>
          ) : null}
        </PainelCorpo>
      </Painel>

      <section
        className="grid gap-6 lg:grid-cols-2"
        aria-label="Distribuição multi-rede"
      >
        <Painel>
          <PainelCabecalho
            titulo="Por plataforma"
            descricao="Contagens da agenda multi-rede."
          />
          <PainelCorpo>
            {plataformas.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhuma publicação na agenda ainda.
              </p>
            ) : (
              <ul className="flex flex-col gap-3">
                {plataformas.map(([plataforma, qtd]) => (
                  <li
                    key={plataforma}
                    className="flex items-center justify-between gap-3 rounded-[var(--rk-radius-md)] border border-border/70 bg-muted/30 px-4 py-3"
                  >
                    <span className="inline-flex items-center gap-2 text-sm font-medium">
                      <Send className="size-3.5 text-muted-foreground" aria-hidden="true" />
                      {ROTULOS_PLATAFORMA[plataforma] ?? plataforma}
                    </span>
                    <span className="text-sm font-semibold tabular-nums">
                      {qtd}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </PainelCorpo>
        </Painel>

        <Painel>
          <PainelCabecalho
            titulo="Por status"
            descricao="Fila operacional da agenda."
          />
          <PainelCorpo>
            {status.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem status.</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {status.map(([st, qtd]) => (
                  <li
                    key={st}
                    className="flex items-center justify-between gap-3 rounded-[var(--rk-radius-md)] border border-border/70 bg-muted/30 px-4 py-3"
                  >
                    <ChipStatus tom={st === "failed" ? "alerta" : "neutro"}>
                      {ROTULOS_STATUS[st] ?? st}
                    </ChipStatus>
                    <span className="text-sm font-semibold tabular-nums">
                      {qtd}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </PainelCorpo>
        </Painel>
      </section>

      <Painel>
        <PainelCabecalho
          titulo="Falhas recentes"
          descricao="Últimas 10 da agenda."
        />
        <PainelCorpo semPadding>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-6">Publicação</TableHead>
                <TableHead>Rede</TableHead>
                <TableHead className="pr-6">Motivo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pub.falhasRecentes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="pl-6 text-muted-foreground">
                    Nenhuma falha recente.
                  </TableCell>
                </TableRow>
              ) : (
                pub.falhasRecentes.map((falha) => (
                  <TableRow key={falha.id}>
                    <TableCell className="max-w-[14rem] truncate pl-6 font-medium">
                      {falha.nome}
                    </TableCell>
                    <TableCell>
                      {ROTULOS_PLATAFORMA[falha.plataforma] ?? falha.plataforma}
                    </TableCell>
                    <TableCell className="pr-6 text-sm text-muted-foreground">
                      {falha.motivoFalha ?? "Sem detalhe"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </PainelCorpo>
      </Painel>
    </div>
  );
}
